/* eslint-disable */

import { CircularProgress, Typography, Icon, Modal } from "@mui/material";

import MDBox from "components/MDBox";
import ModalTitle from "examples/NewDesign/ModalTitle";
import { Colors, ButtonTitles, Icons } from "utils/Constants";
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { getCibilScore, fetchCibilScore } from "redux/Thunks/CibilThunk";
import { useDispatch } from "react-redux";
import MDButton from "components/MDButton";
import moment from "moment";
import { CreditReportContent, normalizeReport } from "./CreditScore";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "min(1120px, calc(100vw - 32px))",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: "8px",
  p: 0,
};

const FETCH_COOLDOWN_DAYS = 90;
const FETCH_COOLDOWN_MS = FETCH_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;

// --- helpers ----------------------------------------------------

const latestTimestamp = (item) => {
  // prefer DB fetchOn date; else score date; else createdAt; else 0
  const a = item?.fetchOn ? new Date(item.fetchOn).getTime() : 0;
  const b = (() => {
    const base = item?.response?.data || item?.response || {};
    const creditReport = Array.isArray(base?.credit_report)
      ? base.credit_report[0] || {}
      : base?.credit_report || {};
    const d =
      creditReport?.CreditProfileHeader?.ReportDate || creditReport?.scores?.[0]?.scoreDate || "";
    const normalizedDate = d ? String(d).trim() : "";

    if (normalizedDate && /^\d{8}$/.test(normalizedDate)) {
      const looksLikeYmd = normalizedDate.startsWith("19") || normalizedDate.startsWith("20");
      const yyyy = looksLikeYmd ? normalizedDate.slice(0, 4) : normalizedDate.slice(4);
      const mm = looksLikeYmd ? normalizedDate.slice(4, 6) : normalizedDate.slice(2, 4);
      const dd = looksLikeYmd ? normalizedDate.slice(6, 8) : normalizedDate.slice(0, 2);
      return new Date(`${yyyy}-${mm}-${dd}T00:00:00Z`).getTime();
    }

    const parsedTime = normalizedDate ? new Date(normalizedDate).getTime() : 0;
    return Number.isNaN(parsedTime) ? 0 : parsedTime;
  })();
  const c = item?.createdAt ? new Date(item.createdAt).getTime() : 0;
  return Math.max(a, b, c);
};

const canFetchFreshReport = (reports = []) => {
  if (!Array.isArray(reports) || reports.length === 0) return true;

  const latestReportTime = latestTimestamp(reports[0]);
  if (!latestReportTime) return true;

  return Date.now() - latestReportTime >= FETCH_COOLDOWN_MS;
};

// --- main component ---------------------------------------------
function ViewCibil({ open, loanFormId, name, mobile, gender, pan, handleClose }) {
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState({
    data: false,
    pdfs: false,
  });
  const [pdfReports, setPdfReports] = useState([]);
  const [cibilReports, setCibilReports] = useState([]);
  const dispatch = useDispatch();

  const normalizedReports = useMemo(
    () =>
      cibilReports.map((item) => {
        const fetchedBy = item?.fetchBy
          ? `${item.fetchBy.firstName ?? ""} ${item.fetchBy.lastName ?? ""}`.trim()
          : "";

        return normalizeReport(item?.response?.data || item?.response || {}, { fullName: name, mobile, pan }, gender, {
          fetchDate: item?.fetchOn,
          fetchedBy,
        });
      }),
    [cibilReports, gender, mobile, name, pan]
  );

  const handleDownload = async (url, pdfName) => {
    try {
      const res = await fetch(url, { mode: "cors" });
      const blob = await res.blob();

      // Ensure the blob is treated as a PDF
      const pdfBlob = new Blob([blob], { type: "application/pdf" });

      const link = document.createElement("a");
      link.href = URL.createObjectURL(pdfBlob);
      link.download = pdfName.endsWith(".pdf") ? pdfName : `${pdfName}.pdf`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  useEffect(() => {
    (async () => {
      if (!open) return;
      setLoading(true);
      try {
        const res = await dispatch(getCibilScore(loanFormId));

        if (res.payload.status !== 200) return;
        // adapt to your thunk’s return shape
        const payload = res?.payload.data;

        const dataArray = payload?.data || [];
        const pdfsArray = payload?.pdfUrls || [];

        // sort latest first
        dataArray.sort((a, b) => latestTimestamp(b) - latestTimestamp(a));
        pdfsArray.sort((a, b) => latestTimestamp(b) - latestTimestamp(a));

        setCibilReports(dataArray);
        setPdfReports(pdfsArray);
      } catch (e) {
        setCibilReports([]);
        setPdfReports([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, loanFormId, dispatch]);

  const handleCibilFetch = async (type = "data") => {
    // check if any of the fields are empty
    if (!name || !mobile || !gender || !pan) {
      let emptyField = [];
      if (!name) emptyField.push("Name");
      if (!mobile) emptyField.push("Mobile Number");
      if (!gender) emptyField.push("Gender");
      if (!pan) emptyField.push("PAN Number");
      emptyField = emptyField.join(", ");
      alert(`${emptyField} is required to fetch CIBIL score`);
      return;
    }

    setFetchLoading({
      data: type === "data",
      pdfs: type === "pdf",
    });

    const body = {
      payload: {
        mobile,
        pan,
        name,
        gender,
        consent: "Y",
      },
      type,
      formId: loanFormId,
    };

    const res = await dispatch(fetchCibilScore({ body }));
    setFetchLoading({
      data: false,
      pdfs: false,
    });
    if (res.payload.status !== 201) return;
    const payload = res?.payload.data.record;
    const dataArray = payload?.data || [];
    const pdfsArray = payload?.pdfUrls || [];
    dataArray.sort((a, b) => latestTimestamp(b) - latestTimestamp(a));
    pdfsArray.sort((a, b) => latestTimestamp(b) - latestTimestamp(a));
    setCibilReports(dataArray);
    setPdfReports(pdfsArray);
  };

  const canFetchData = !loading && !fetchLoading.data && canFetchFreshReport(cibilReports);
  const canFetchPdf = !loading && !fetchLoading.pdfs && canFetchFreshReport(pdfReports);

  return (
    <Modal
      open={open}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      onClose={handleClose}
    >
      <MDBox sx={style}>
        <MDBox
          bgColor={Colors.PRIMARY}
          p={3}
          mb={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          borderRadius="lg"
        >
          <ModalTitle title="View Credit Score" color="white" />
          <Icon sx={{ cursor: "pointer", color: "beige" }} fontSize="medium" onClick={handleClose}>
            {Icons.CROSS}
          </Icon>
        </MDBox>

        {/* Max height of modal is 80vh and then scrollbar */}
        <MDBox sx={{ overflowY: "auto", maxHeight: "70vh", p: 1, mt: 2 }}>
          {loading ? (
            <MDBox py={5} display="flex" justifyContent="center" alignItems="center">
              <CircularProgress color="info" />
            </MDBox>
          ) : normalizedReports.length > 0 ? (
            <MDBox display="flex" flexDirection="column" gap={2}>
              {normalizedReports.map((report, index) => (
                <CreditReportContent
                  key={`${report.reportDate}-${report.fetchDate}-${index}`}
                  report={report}
                />
              ))}
            </MDBox>
          ) : (
            <MDBox p={3}>
              <Typography variant="body2">No CIBIL reports found.</Typography>
            </MDBox>
          )}

          {!loading && (
            <MDBox px={2} pb={2}>
              <Typography variant="h6" fontWeight="bold" mb={2}>
                PDF Reports
              </Typography>
              <MDBox
                display="grid"
                gridTemplateColumns="repeat(auto-fill, minmax(260px, 1fr))"
                gap={2.5}
              >
                {pdfReports.length > 0 ? (
                  pdfReports.map((pdf, index) => (
                    <MDBox
                      key={pdf.cibilId}
                      p={2}
                      borderRadius="14px"
                      bgcolor="#fff"
                      border="1px solid #e0e0e0"
                      sx={{
                        backgroundImage: "linear-gradient(145deg, #ffffff, #f9f9f9)",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          border: `1px solid ${Colors.PRIMARY}`,
                          boxShadow: "0 6px 14px rgba(0,0,0,0.15)",
                        },
                      }}
                    >
                      <MDBox mb={1}>
                        <Typography variant="subtitle1" fontWeight="bold" noWrap>
                          📄 {pdf.response.name || `CIBIL Report ${index + 1}`}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" display="block">
                          📅 {moment(pdf.fetchOn).format("DD-MM-YYYY")}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" display="block">
                          👤 {pdf.fetchBy.firstName} {pdf.fetchBy.lastName}
                        </Typography>
                      </MDBox>
                      <MDButton
                        variant="contained"
                        color="info"
                        onClick={() =>
                          handleDownload(pdf.response.credit_report_link, pdf.response.name)
                        }
                        style={{
                          textTransform: "none",
                          alignSelf: "flex-end",
                          borderRadius: "8px",
                          padding: "4px 12px",
                          fontSize: "13px",
                          background: Colors.PRIMARY,
                          color: Colors.WHITE,
                        }}
                      >
                        Download
                      </MDButton>
                    </MDBox>
                  ))
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No PDFs available
                  </Typography>
                )}
              </MDBox>
            </MDBox>
          )}
        </MDBox>

        {/* Fetch data and pdf buttons */}
        <MDBox px={2} mb={2} mt={1}>
          <MDBox display="flex" justifyContent="flex-end" flexWrap="wrap" gap={1.25}>
            <MDButton
              variant="contained"
              color="info"
              disabled={!canFetchData}
              onClick={() => handleCibilFetch("data")}
              style={{
                textTransform: "none",
                boxShadow: "none",
                background: Colors.WHITE,
                color: Colors.PRIMARY,
                border: `1px solid ${Colors.PRIMARY}`,
              }}
            >
              {fetchLoading.data ? ButtonTitles.FETHING : "Fetch New Data"}
            </MDButton>
            <MDButton
              variant="contained"
              color="info"
              disabled={!canFetchPdf}
              onClick={() => handleCibilFetch("pdf")}
              style={{
                textTransform: "none",
                boxShadow: "none",
                background: Colors.PRIMARY,
                color: Colors.WHITE,
              }}
            >
              {fetchLoading.pdfs ? ButtonTitles.FETHING : ButtonTitles.FETCH_NEW_PDF}
            </MDButton>
          </MDBox>
          <Typography
            variant="caption"
            color="textSecondary"
            display="block"
            textAlign="right"
            mt={1}
          >
            Fresh CIBIL data and PDF can be fetched again only after 90 days from their respective
            latest fetch date.
          </Typography>
        </MDBox>
      </MDBox>
    </Modal>
  );
}

ViewCibil.propTypes = {
  open: PropTypes.bool.isRequired,
  loanFormId: PropTypes.string.isRequired,
  handleClose: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  mobile: PropTypes.string.isRequired,
  gender: PropTypes.string.isRequired,
  pan: PropTypes.string.isRequired,
};

export default ViewCibil;
